/**
 * Attendance history — every recorded session, filterable, exportable.
 *
 * The pickup roster is a board for running today's dismissal and shows one week. This is the
 * archive, and it exists for the questions that arrive afterwards: who collected this child
 * on the 14th, which handovers went to someone not on the authorized list, how often was this
 * family absent last term. Those questions are asked days or weeks late, by which time the
 * roster has moved on.
 *
 * One row per attendance record, **not** one per child per day. A child in after-school also
 * has a Chinese row and a Math row for the same date, and collapsing them would make a class
 * register indistinguishable from a pickup handover. The Class column is what tells them
 * apart, and it reads the `virtual_class_key` rather than the program name — a virtual row
 * carries the child's after-school program id, so the program name alone would label all
 * three "Regular G1-G5".
 */

import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Search } from 'lucide-react';
import {
  AttendanceHistoryTable,
  type AttendanceHistorySection,
} from '../../components/attendance/AttendanceHistoryTable';
import { AttendanceRecordCard } from '../../components/attendance/AttendanceRecordCard';
import {
  EXPORT_ROW_CAP,
  HISTORY_BAND_LABEL,
  HISTORY_BAND_ORDER,
  HISTORY_PAGE_SIZE,
  fetchAllAttendanceHistory,
  useAttendanceFacets,
  useAttendanceHistory,
  type AttendanceHistoryFilters,
  type HistoryBand,
} from '../../hooks/useAttendanceHistory';
import {
  MISSING_VALUE,
  facetSelectionFor,
  type FacetOption,
} from '../../utils/attendanceFacets';
import {
  HISTORY_STATUSES,
  HISTORY_STATUS_LABEL,
  attendanceClassLabel,
  attendanceHistoryFilename,
  buildAttendanceHistoryCsv,
  type AttendanceHistoryRow,
} from '../../utils/attendanceHistory';
import { buildAttendanceSegments, virtualGradeOptions } from '../../utils/attendanceSegments';
import { VIRTUAL_GRADES } from '../../staff-app/virtualClasses';
import { formatGradeLabel } from '../../utils/rosterGrouping';
import { getTodayDateStringInBusinessTZ } from '../../utils/date';
import { shiftDateString } from '../../utils/schedule';
import { downloadCSV } from '../../utils/csv';
import { analytics } from '../../utils/analytics';
import type { AttendanceStatus } from '../../types/attendance';

/** How far back the page opens on. A month covers "what happened recently" without a wait. */
const DEFAULT_RANGE_DAYS = 30;

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && 'message' in e) {
    const message = (e as { message: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  return 'Something went wrong.';
}

/**
 * The line under a name on a card, where there are no columns to put it in.
 *
 * The table answers "which class" in a column and hides grade behind the chevron. A card
 * has the width to say both outright, so it does — a record you have to tap to identify is
 * a record you scroll past.
 */
function cardContext(row: AttendanceHistoryRow): string | null {
  const grade = row.studentGrade?.trim();
  return [
    attendanceClassLabel(row.virtualClassKey, row.programName),
    grade ? formatGradeLabel(grade) : null,
    row.studentSchool?.trim() || null,
  ].filter(Boolean).join(' · ') || null;
}

export function AdminAttendanceHistory() {
  const today = getTodayDateStringInBusinessTZ();
  const [from, setFrom] = useState(() => shiftDateString(today, -DEFAULT_RANGE_DAYS));
  const [to, setTo] = useState(today);
  // After School by default: the year-round programme, and the one whose sections carry the
  // most. There is no "all bands" — see `HistoryBand`.
  const [band, setBand] = useState<HistoryBand>('afterschool');
  const [status, setStatus] = useState<AttendanceStatus | 'all'>('all');
  const [unauthorizedOnly, setUnauthorizedOnly] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [collectorSearch, setCollectorSearch] = useState('');
  // After School only: which grade band, and which school.
  const [bandGradeValue, setBandGradeValue] = useState('all');
  const [schoolValue, setSchoolValue] = useState('all');
  // Chinese/Math only: one of the seven grades those classes run, as a virtual_class_key.
  const [virtualGrade, setVirtualGrade] = useState('all');
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);

  const { data: facets } = useAttendanceFacets();

  /** Bands and the grades under them, flattened — one list the selection resolves against. */
  const gradeOptions: FacetOption[] = useMemo(
    () => [...(facets?.bands ?? []), ...(facets?.grades ?? [])],
    [facets],
  );

  const filters: AttendanceHistoryFilters = useMemo(
    () => ({
      from,
      to,
      band,
      status,
      unauthorizedOnly,
      studentSearch,
      collectorSearch,
      // Grade and school narrow After School only — they are the axes its dismissal runs on.
      // Chinese and Math narrow by picking one of the fourteen classes instead, which is the
      // same question asked of a column that already holds the answer.
      grade: band === 'afterschool' ? facetSelectionFor(bandGradeValue, gradeOptions) : { kind: 'all' },
      school: band === 'afterschool' ? facetSelectionFor(schoolValue, facets?.schools ?? []) : { kind: 'all' },
      virtualClassKey: (band === 'chinese' || band === 'math') && virtualGrade !== 'all'
        ? virtualGrade
        : null,
    }),
    [from, to, band, status, unauthorizedOnly, studentSearch, collectorSearch,
     bandGradeValue, gradeOptions, schoolValue, facets?.schools, virtualGrade],
  );

  const { data, isLoading, isFetching, error } = useAttendanceHistory(filters, page);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  /**
   * Sections for the rows on screen. After School splits into the pickup board's runs
   * (school × grade band); Chinese and Math split by grade, since the class is the grade and
   * every child in the room is from a different school; camps and enrichment do not split.
   */
  const segments = useMemo(() => buildAttendanceSegments(rows, band), [rows, band]);
  /**
   * The same runs, in the shape both layouts read. A band that does not split becomes one
   * unlabelled section rather than a special case, so the table and the card list each have
   * exactly one thing to render.
   */
  const sections: AttendanceHistorySection[] = useMemo(
    () => (segments.length > 0
      ? segments.map(segment => ({
          key: segment.key,
          label: segment.label,
          meta: segment.rows.length,
          rows: segment.rows,
        }))
      : [{ key: 'all', label: null, rows }]),
    [segments, rows],
  );
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / HISTORY_PAGE_SIZE) - 1);
  const rangeInvalid = from > to;

  /** Any filter change invalidates the page number — page 4 of a new result set is a lie. */
  function change<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(0);
      setExpanded(null);
    };
  }

  function resetFilters() {
    setFrom(shiftDateString(today, -DEFAULT_RANGE_DAYS));
    setTo(today);
    setBand('afterschool');
    setStatus('all');
    setUnauthorizedOnly(false);
    setStudentSearch('');
    setCollectorSearch('');
    setBandGradeValue('all');
    setSchoolValue('all');
    setVirtualGrade('all');
    setPage(0);
    setExpanded(null);
    setExportNote(null);
  }

  async function handleExport() {
    setExporting(true);
    setExportNote(null);
    try {
      const { rows: allRows, truncated } = await fetchAllAttendanceHistory(filters);
      if (allRows.length === 0) {
        setExportNote('Nothing to export — no records match these filters.');
        return;
      }
      downloadCSV(buildAttendanceHistoryCsv(allRows), attendanceHistoryFilename(from, to));
      setExportNote(
        truncated
          ? `Exported the first ${EXPORT_ROW_CAP.toLocaleString()} records. Narrow the date range to get the rest.`
          : `Exported ${allRows.length.toLocaleString()} record${allRows.length === 1 ? '' : 's'}.`,
      );
      analytics.trackAdminAction('admin_attendance_history_export', {
        entityType: 'attendance_record',
        metadata: {
          from,
          to,
          rows: allRows.length,
          truncated,
          band,
          grade: bandGradeValue,
          school: schoolValue,
          virtual_grade: virtualGrade,
          status,
          unauthorized_only: unauthorizedOnly,
        },
      });
    } catch (e) {
      setExportNote(`Export failed: ${errorMessage(e)}`);
    } finally {
      setExporting(false);
    }
  }

  const firstShown = total === 0 ? 0 : page * HISTORY_PAGE_SIZE + 1;
  const lastShown = Math.min(total, page * HISTORY_PAGE_SIZE + rows.length);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-500">
            From
            <input
              type="date"
              value={from}
              onChange={e => change(setFrom)(e.target.value)}
              className="block mt-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
          <label className="text-xs text-gray-500">
            To
            <input
              type="date"
              value={to}
              onChange={e => change(setTo)(e.target.value)}
              className="block mt-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>

          <label className="text-xs text-gray-500">
            Class
            <select
              value={band}
              onChange={e => {
                // The sub-filters below belong to the band that was showing them; carrying
                // one into a band that cannot display it would filter invisibly.
                setBandGradeValue('all');
                setSchoolValue('all');
                setVirtualGrade('all');
                change(setBand)(e.target.value as HistoryBand);
              }}
              className="block mt-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {/* The five bands and their names come from the shared list the teacher
                  portal's own history reads, so the two surfaces cannot drift apart. */}
              {HISTORY_BAND_ORDER.map(option => (
                <option key={option} value={option}>{HISTORY_BAND_LABEL[option]}</option>
              ))}
            </select>
          </label>

          {/* Sub-filters belong to the band above them. After School dismisses by school
              and grade band, so those are its axes; Chinese and Math are one grade each, so
              a grade *is* a class there; camps and enrichment have neither. */}
          {band === 'afterschool' && (
            <>
              <label className="text-xs text-gray-500">
                Grade band
                <select
                  value={bandGradeValue}
                  onChange={e => change(setBandGradeValue)(e.target.value)}
                  className="block mt-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All bands</option>
                  {(facets?.bands ?? []).map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                  {facets?.hasMissingGrade && (
                    // Its own option: a grade that will not normalise puts a child in no
                    // Chinese or Math class at all, and this is how you find them.
                    <option value={MISSING_VALUE}>No grade listed</option>
                  )}
                </select>
              </label>

              <label className="text-xs text-gray-500">
                School
                <select
                  value={schoolValue}
                  onChange={e => change(setSchoolValue)(e.target.value)}
                  className="block mt-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All schools</option>
                  {(facets?.schools ?? []).map(school => (
                    <option key={school.value} value={school.value}>{school.label}</option>
                  ))}
                  {facets?.hasMissingSchool && (
                    <option value={MISSING_VALUE}>No school listed</option>
                  )}
                </select>
              </label>
            </>
          )}

          {(band === 'chinese' || band === 'math') && (
            <label className="text-xs text-gray-500">
              Grade
              <select
                value={virtualGrade}
                onChange={e => change(setVirtualGrade)(e.target.value)}
                className="block mt-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All grades</option>
                {virtualGradeOptions(band, VIRTUAL_GRADES).map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          )}

          <label className="text-xs text-gray-500">
            Status
            <select
              value={status}
              onChange={e => change(setStatus)(e.target.value as AttendanceStatus | 'all')}
              className="block mt-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Any status</option>
              {HISTORY_STATUSES.map(s => (
                <option key={s} value={s}>{HISTORY_STATUS_LABEL[s]}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-500 flex-1 min-w-[12rem]">
            Student
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="search"
                value={studentSearch}
                onChange={e => change(setStudentSearch)(e.target.value)}
                placeholder="Name"
                className="w-full pl-8 pr-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </label>
          <label className="text-xs text-gray-500 flex-1 min-w-[12rem]">
            Collected / checked in by
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="search"
                value={collectorSearch}
                onChange={e => change(setCollectorSearch)(e.target.value)}
                placeholder="Name"
                className="w-full pl-8 pr-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-700 pb-1.5">
            <input
              type="checkbox"
              checked={unauthorizedOnly}
              onChange={e => change(setUnauthorizedOnly)(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Only unauthorized pickups
          </label>

          <button
            type="button"
            onClick={resetFilters}
            className="px-3 py-1.5 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-gray-600">
          {rangeInvalid
            ? 'The From date is after the To date.'
            : isLoading
              ? 'Loading…'
              : `${total.toLocaleString()} record${total === 1 ? '' : 's'}${
                  total > 0 ? ` · showing ${firstShown.toLocaleString()}–${lastShown.toLocaleString()}` : ''
                }`}
          {isFetching && !isLoading && <span className="text-gray-400"> · refreshing</span>}
        </p>
        <div className="ml-auto flex items-center gap-3">
          {exportNote && <span className="text-xs text-gray-500">{exportNote}</span>}
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={exporting || total === 0 || rangeInvalid}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {error ? (
        <p className="p-4 bg-red-50 text-sm text-red-700 rounded-lg">
          Could not load attendance history: {errorMessage(error)}
        </p>
      ) : rangeInvalid ? null : isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-12 bg-gray-100 animate-pulse rounded-lg" />)}
        </div>
      ) : rows.length === 0 ? (
        <p className="p-6 bg-white border border-gray-200 rounded-lg text-sm text-gray-500">
          No attendance records match these filters.
        </p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Eight columns above 1024px, one card per record below it. The same records
              either way — see `AttendanceHistoryTable`, which the teacher's archive renders
              from as well. */}
          <div className="hidden lg:block" data-layout="table">
            <AttendanceHistoryTable
              sections={sections}
              expandedId={expanded}
              onToggleExpanded={setExpanded}
            />
          </div>

          <div className="lg:hidden" data-layout="cards">
            {sections.map(section => (
              <div key={section.key}>
                {section.label !== null && (
                  <div className="px-3 py-2 bg-gray-100/70 text-xs font-semibold text-gray-700">
                    {section.label}
                    <span className="ml-2 font-normal text-gray-400">{section.rows.length}</span>
                  </div>
                )}
                <ul className="divide-y divide-gray-100">
                  {section.rows.map(row => (
                    // The date is on the card here, unlike the teacher's, because this list
                    // runs across the whole range rather than being grouped by day.
                    <AttendanceRecordCard key={row.id} row={row} context={cardContext(row)} showDate />
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {lastPage > 0 && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => { setPage(p => Math.max(0, p - 1)); setExpanded(null); }}
                disabled={page === 0}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-200 bg-white text-sm text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </button>
              <span className="text-xs text-gray-500">Page {page + 1} of {lastPage + 1}</span>
              <button
                type="button"
                onClick={() => { setPage(p => Math.min(lastPage, p + 1)); setExpanded(null); }}
                disabled={page >= lastPage}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-200 bg-white text-sm text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
